/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Button, Toggle } from '@carbon/react';
import { Headset, ResetAlt } from '@carbon/react/icons';
import React, { Fragment, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import {
  ServiceDeskLogo,
  getServiceDeskDescription
} from '../../../helpers/serviceDeskHelpers';
import { useWebChatFeatures } from '../../../hooks/useChannelFeatures';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  channelTypeToChannelName,
  webChatServiceDeskIntegrations,
  ootbWebChatServiceDeskIntegrations,
  GENESYS_SERVICEDESK_TYPE,
  SALESFORCE_SERVICEDESK_TYPE,
  WEBCHAT_CHANNEL_TYPE,
  ZENDESK_SERVICEDESK_TYPE,
  NICE_INCONTACT_BYOSD_TYPE,
  TWILIO_BYOSD_TYPE
} from '../../../util/channelTypes';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { ServiceDeskResetModal } from '../../modals/ServiceDeskResetModal';
import { GenesysCard } from '../../service-desk/genesys/GenesysCard';
import { GitHubCard } from '../../service-desk/GitHubCard';
import { NiceCard } from '../../service-desk/nice/NiceCard';
import { Salesforce } from '../../service-desk/salesforce/Salesforce';
import { ServiceDeskCard } from '../../service-desk/ServiceDeskCard';
import { Zendesk } from '../../service-desk/zendesk/Zendesk';
import { links } from '../links';
import { LiveAgentTab as messages } from '../messages';
import { FIELDS } from '../schema';

import './LiveAgent.scss';

interface LiveAgentProps {
  /**
   *
   */
  tabname?: string;
  /**
   *
   */
  assistantId?: string;
  /**
   *
   */
  environmentId?: string;
  /**
   *
   */
  readOnly?: boolean;
}
export const LiveAgent = ({
  assistantId,
  environmentId,
  readOnly
}: LiveAgentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const supportedChannels = useWebChatFeatures();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  //TODO: add segment tracking to toggle.
  const toggled = getFieldValue(FIELDS.SERVICE_DESK.IS_ON);
  const serviceDeskId = getFieldValue(FIELDS.SERVICE_DESK.INTEGRATION_ID);
  const selectedServiceDesk = getFieldValue(
    FIELDS.SERVICE_DESK.INTEGRATION_TYPE
  );

  const clearServiceDeskSettings = useCallback(() => {
    updateFieldValue(FIELDS.SERVICE_DESK.IS_ON, false);
    updateFieldValue(FIELDS.SERVICE_DESK.INTEGRATION_ID, '');
    updateFieldValue(FIELDS.SERVICE_DESK.INTEGRATION_TYPE, '');
  }, [updateFieldValue]);

  const openDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const resetServiceDesk = event => {
    if (serviceDeskId) {
      openDeleteModal();
    } else {
      event.preventDefault();
      // return to change service desk screen
      clearServiceDeskSettings();
    }
  };

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  // Select which service desk is to be used.
  const onChangeSelectedServiceDesk = useCallback(
    (changeServiceDesk: string) => {
      clearServiceDeskSettings();
      updateFieldValue(
        FIELDS.SERVICE_DESK.INTEGRATION_TYPE,
        changeServiceDesk ?? ''
      );
      ctaEvents.serviceDeskSelected({
        category: channelTypeToChannelName[changeServiceDesk]
      });
    },
    [clearServiceDeskSettings, updateFieldValue]
  );

  // Update service_desk.integration_id when appropriate
  const updateServiceDeskId = useCallback(
    (integrationID: string) => {
      updateFieldValue(FIELDS.SERVICE_DESK.INTEGRATION_ID, integrationID ?? '');
    },
    [updateFieldValue]
  );

  const learnMoreLink = useIsNewIA()
    ? links.learnMoreLiveAgentLinkNewIA
    : links.learnMoreLiveAgentLinkClassic;

  // These titles are hidden when selected as a service desk because their icons says the name of the service desk
  const hiddenTitleServiceDesks = [
    GENESYS_SERVICEDESK_TYPE,
    NICE_INCONTACT_BYOSD_TYPE,
    TWILIO_BYOSD_TYPE
  ];

  const onToggle = async boolean => {
    updateFieldValue(FIELDS.SERVICE_DESK.IS_ON, boolean);
  };

  return (
    supportedChannels.length > 0 && (
      <Fragment>
        <div className="LiveAgent-ServiceDesk__choose-new">
          <TabContent
            ImageComponent={() => <Headset size={32} />}
            title={<FormattedMessage {...messages.title} />}
            subtitle={t(messages.subtitle)}
            learnMoreLink={learnMoreLink}>
            <div className="LiveAgent LiveAgent-ServiceDesk__config">
              {!selectedServiceDesk ? (
                <div className="ServiceDesk__tiles">
                  <div className="ServiceDesk__tile-group">
                    {supportedChannels.map(serviceDesk => (
                      <ServiceDeskCard
                        readOnly={readOnly}
                        key={serviceDesk}
                        onClick={() => onChangeSelectedServiceDesk(serviceDesk)}
                        serviceDesk={serviceDesk}
                        parentChannelType={WEBCHAT_CHANNEL_TYPE}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="LiveAgent-ServiceDesk__title">
                    <ServiceDeskLogo
                      serviceDeskType={selectedServiceDesk}
                      parentChannelType={WEBCHAT_CHANNEL_TYPE}
                      showTitle={
                        !hiddenTitleServiceDesks.includes(selectedServiceDesk)
                      }
                    />
                  </div>
                  <p className="TabContent__subtitle">
                    {getServiceDeskDescription(
                      WEBCHAT_CHANNEL_TYPE,
                      selectedServiceDesk,
                      t
                    )}
                  </p>
                  {ootbWebChatServiceDeskIntegrations.includes(
                    selectedServiceDesk
                  ) && (
                    <div className="TabContent__field-container">
                      <Toggle
                        id={FIELDS.SERVICE_DESK.IS_ON}
                        disabled={readOnly}
                        onToggle={onToggle}
                        toggled={getFieldValue(FIELDS.SERVICE_DESK.IS_ON)}
                        aria-label={t(messages.enable)}
                        labelA={t(messages.transfersDisabled)}
                        labelB={t(messages.transfersEnabled)}
                        size="sm"
                      />
                    </div>
                  )}
                  <Button
                    className="LiveAgent-ServiceDesk__change-other"
                    disabled={readOnly}
                    hasIconOnly={false}
                    kind="ghost"
                    size="sm"
                    aria-label={t(messages.changeLabel)}
                    iconDescription={t(messages.changeLabel)}
                    onClick={resetServiceDesk}>
                    <p>
                      <FormattedMessage {...messages.changeLabel} />
                    </p>
                    <ResetAlt
                      size={16}
                      aria-label={t(messages.changeLabel)}
                      className="LiveAgent-ServiceDesk__change-other-icon"
                    />
                  </Button>
                  {selectedServiceDesk === SALESFORCE_SERVICEDESK_TYPE && (
                    <>
                      {toggled && (
                        <Salesforce
                          assistantId={assistantId}
                          environmentId={environmentId}
                          readOnly={readOnly}
                          serviceDeskId={serviceDeskId}
                          updateServiceDeskId={updateServiceDeskId}
                        />
                      )}
                    </>
                  )}
                  {selectedServiceDesk === ZENDESK_SERVICEDESK_TYPE && (
                    <>
                      {toggled && (
                        <Zendesk
                          assistantId={assistantId}
                          environmentId={environmentId}
                          readOnly={readOnly}
                          serviceDeskId={serviceDeskId}
                          updateServiceDeskId={updateServiceDeskId}
                          webChatFormMethods={formMethods}
                        />
                      )}
                    </>
                  )}
                  {selectedServiceDesk === GENESYS_SERVICEDESK_TYPE && (
                    <GenesysCard />
                  )}
                  {selectedServiceDesk === NICE_INCONTACT_BYOSD_TYPE && (
                    <NiceCard />
                  )}
                  {!webChatServiceDeskIntegrations.includes(
                    selectedServiceDesk
                  ) && (
                    <Fragment>
                      <GitHubCard selectedServiceDesk={selectedServiceDesk} />
                    </Fragment>
                  )}
                </div>
              )}
            </div>
          </TabContent>
        </div>
        {webChatServiceDeskIntegrations.includes(selectedServiceDesk) && (
          <ServiceDeskResetModal
            assistantId={assistantId}
            environmentId={environmentId}
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            readOnly={readOnly}
            serviceDeskId={serviceDeskId}
          />
        )}
      </Fragment>
    )
  );
};
